import React, { useEffect, useMemo, useState } from "react";

import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Divider,
    Empty,
    List,
    Modal,
    Row,
    Space,
    Spin,
    Tag,
    Typography,
    message,
} from "antd";

import {
    BookOutlined,
    CheckCircleOutlined,
    FilePdfOutlined,
    FileTextOutlined,
    ExperimentOutlined,
    ReloadOutlined,
    RobotOutlined,
    SafetyCertificateOutlined,
    SolutionOutlined,
} from "@ant-design/icons";

import api from "../api/api";

import AiTeacherNotesPDF from "./AiLessonPlan/AiTeacherNotesPDF";
import AiLessonPlanPDF from "./AiLessonPlan/AiLessonPlanPDF";


const { Title, Text, Paragraph } = Typography;

const COLORS = {
    navy: "#1B2A41",
    khaki: "#C3B091",
    green: "#16a34a",
    orange: "#f97316",
    blue: "#2563eb",
    red: "#dc2626",
    gray: "#64748b",
    light: "#f8fafc",
    border: "#e2e8f0",
    white: "#ffffff",
};

const normalizeText = (value) => {
    if (value === null || value === undefined) {
        return "";
    }

    if (typeof value === "string") {
        return value.trim();
    }

    if (typeof value === "number" || typeof value === "boolean") {
        return String(value);
    }

    return "";
};

const isObject = (value) =>
    value !== null &&
    typeof value === "object" &&
    !Array.isArray(value);

const isArray = (value) => Array.isArray(value);

const safeArray = (value) => {
    if (Array.isArray(value)) {
        return value;
    }

    if (value === null || value === undefined) {
        return [];
    }

    return [value];
};

const getApiErrorMessage = (error, fallback) => {
    const responseData = error?.response?.data;

    if (typeof responseData === "string" && responseData.trim()) {
        return responseData.trim();
    }

    if (responseData?.error) {
        if (typeof responseData.error === "string") {
            return responseData.error;
        }

        if (isObject(responseData.error)) {
            return Object.entries(responseData.error)
                .map(([key, value]) => {
                    if (Array.isArray(value)) {
                        return `${key}: ${value.join(", ")}`;
                    }

                    return `${key}: ${String(value)}`;
                })
                .join(" | ");
        }
    }

    if (responseData?.detail) {
        return String(responseData.detail);
    }

    if (responseData?.message) {
        return String(responseData.message);
    }

    if (error?.message) {
        return error.message;
    }

    return fallback;
};

const getPdfUrl = (data) => {
    if (!data || typeof data !== "object") {
        return "";
    }

    const candidates = [
        data.pdf_url,
        data.pdf,
        data.download_url,
        data.file_url,
        data.file,
    ];

    for (const value of candidates) {
        if (typeof value === "string" && value.trim()) {
            return value.trim();
        }

        if (value && typeof value === "object") {
            if (
                typeof value.url === "string" &&
                value.url.trim()
            ) {
                return value.url.trim();
            }
        }
    }

    return "";
};

const getAiContent = (data) => {
    if (!data || typeof data !== "object") {
        return {};
    }

    if (
        isObject(data.generated_content)
    ) {
        return data.generated_content;
    }

    if (
        isObject(data.lesson_content)
    ) {
        return data.lesson_content;
    }

    if (
        isObject(data.raw_response)
    ) {
        return data.raw_response;
    }

    return data;
};

const getTopicId = (topic) =>
    topic?.id ??
    topic?.topic_id ??
    topic?.topic?.id ??
    null;

const getTopicName = (topic) =>
    normalizeText(
        topic?.name ??
        topic?.topic_name ??
        topic?.topic?.name
    );

const getSubjectName = (topic) =>
    normalizeText(
        topic?.subject_name ??
        topic?.subject?.name ??
        topic?.period?.subject?.name
    );

const getGradeName = (topic) =>
    normalizeText(
        topic?.grade_name ??
        topic?.grade_class_name ??
        topic?.grade_class?.name ??
        topic?.period?.grade_class?.name
    );

const getSemesterName = (topic) =>
    normalizeText(
        topic?.semester_name ??
        topic?.semester ??
        topic?.period?.semester ??
        topic?.period?.semester_name
    );

const getPeriodName = (topic) => {
    const value =
        topic?.period_name ??
        topic?.period?.name ??
        topic?.period?.period_number ??
        topic?.period_number;

    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return "";
    }

    if (typeof value === "number") {
        return `Period ${value}`;
    }

    const text = String(value).trim();

    if (!text) {
        return "";
    }

    if (/^period\b/i.test(text)) {
        return text;
    }

    return `Period ${text}`;
};

const getCurriculumName = (topic) =>
    normalizeText(
        topic?.curriculum_name ??
        topic?.curriculum?.name ??
        topic?.period?.curriculum?.name
    );

const getList = (value) => {
    if (Array.isArray(value)) {
        return value.filter(
            (item) =>
                item !== null &&
                item !== undefined &&
                normalizeText(
                    typeof item === "object"
                        ? (
                            item.text ??
                            item.content ??
                            item.title ??
                            item.description ??
                            ""
                        )
                        : item
                )
        );
    }

    if (
        typeof value === "string" &&
        value.trim()
    ) {
        return [value.trim()];
    }

    return [];
};

const valueToText = (value) => {
    if (
        value === null ||
        value === undefined
    ) {
        return "";
    }

    if (
        typeof value === "string" ||
        typeof value === "number" ||
        typeof value === "boolean"
    ) {
        return String(value).trim();
    }

    if (Array.isArray(value)) {
        return value
            .map((item) => valueToText(item))
            .filter(Boolean)
            .join(" ");
    }

    if (isObject(value)) {
        const preferredKeys = [
            "text",
            "content",
            "passage",
            "example",
            "description",
            "title",
            "name",
            "value",
        ];

        for (const key of preferredKeys) {
            const text = valueToText(value[key]);

            if (text) {
                return text;
            }
        }
    }

    return "";
};

const renderSimpleList = (
    value,
    emptyText = "No information provided."
) => {
    const items = getList(value);

    if (!items.length) {
        return (
            <Text type="secondary">
                {emptyText}
            </Text>
        );
    }

    return (
        <List
            size="small"
            dataSource={items}
            renderItem={(item, index) => (
                <List.Item
                    key={`item-${index}`}
                    style={{
                        padding: "7px 0",
                        alignItems: "flex-start",
                    }}
                >
                    <Space
                        align="start"
                        size={8}
                    >
                        <CheckCircleOutlined
                            style={{
                                color: COLORS.green,
                                marginTop: 4,
                            }}
                        />

                        <Text>
                            {valueToText(item)}
                        </Text>
                    </Space>
                </List.Item>
            )}
        />
    );
};

const renderFlexibleValue = (
    value,
    emptyText = "No information provided."
) => {
    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return (
            <Text type="secondary">
                {emptyText}
            </Text>
        );
    }

    if (Array.isArray(value)) {
        return renderSimpleList(
            value,
            emptyText
        );
    }

    if (
        typeof value === "string" ||
        typeof value === "number" ||
        typeof value === "boolean"
    ) {
        return (
            <Paragraph
                style={{
                    marginBottom: 0,
                    whiteSpace: "pre-wrap",
                }}
            >
                {String(value)}
            </Paragraph>
        );
    }

    if (isObject(value)) {
        const entries = Object.entries(value)
            .filter(
                ([, item]) =>
                    item !== null &&
                    item !== undefined &&
                    item !== ""
            );

        if (!entries.length) {
            return (
                <Text type="secondary">
                    {emptyText}
                </Text>
            );
        }

        return (
            <Space
                direction="vertical"
                size={8}
                style={{ width: "100%" }}
            >
                {entries.map(([key, item]) => (
                    <div key={key}>
                        <Text
                            strong
                            style={{
                                textTransform:
                                    "capitalize",
                            }}
                        >
                            {key
                                .replace(
                                    /_/g,
                                    " "
                                )}
                            :
                        </Text>

                        <div
                            style={{
                                marginTop: 3,
                            }}
                        >
                            {renderFlexibleValue(
                                item,
                                emptyText
                            )}
                        </div>
                    </div>
                ))}
            </Space>
        );
    }

    return (
        <Text type="secondary">
            {emptyText}
        </Text>
    );
};

const CurriculumSection = ({
    title,
    icon,
    children,
    extra,
}) => (
    <Card
        size="small"
        title={
            <Space size={8}>
                {icon}
                <span>{title}</span>
            </Space>
        }
        extra={extra}
        style={{
            marginBottom: 16,
            borderColor: COLORS.border,
            borderRadius: 10,
        }}
        styles={{
            header: {
                background: COLORS.light,
                borderBottomColor:
                    COLORS.border,
            },
            body: {
                padding: 16,
            },
        }}
    >
        {children}
    </Card>
);

const LessonProcedureStage = ({
    stage,
    index,
}) => {
    if (!stage || typeof stage !== "object") {
        return null;
    }

    const stageName =
        normalizeText(
            stage.stage ??
            stage.name ??
            `Stage ${index + 1}`
        );

    const duration =
        normalizeText(stage.duration);

    return (
        <Card
            size="small"
            style={{
                marginBottom: 12,
                borderColor: COLORS.border,
                borderRadius: 8,
            }}
        >
            <div
                style={{
                    display: "flex",
                    justifyContent:
                        "space-between",
                    alignItems:
                        "flex-start",
                    gap: 12,
                    flexWrap: "wrap",
                    marginBottom: 10,
                }}
            >
                <Space>
                    <Badge
                        count={index + 1}
                        style={{
                            backgroundColor:
                                COLORS.navy,
                        }}
                    />

                    <Text strong>
                        {stageName}
                    </Text>
                </Space>

                {duration && (
                    <Tag>
                        {duration}
                    </Tag>
                )}
            </div>

            {stage.objective && (
                <div style={{ marginBottom: 10 }}>
                    <Text strong>
                        Stage Objective
                    </Text>

                    <Paragraph
                        style={{
                            marginBottom: 0,
                            marginTop: 3,
                        }}
                    >
                        {valueToText(
                            stage.objective
                        )}
                    </Paragraph>
                </div>
            )}

            <Row gutter={[16, 12]}>
                <Col
                    xs={24}
                    md={12}
                >
                    <Text strong>
                        Teacher Activities
                    </Text>

                    <div style={{ marginTop: 4 }}>
                        {renderSimpleList(
                            stage.teacher_activities ??
                            stage.teacher_actions
                        )}
                    </div>
                </Col>

                <Col
                    xs={24}
                    md={12}
                >
                    <Text strong>
                        Student Activities
                    </Text>

                    <div style={{ marginTop: 4 }}>
                        {renderSimpleList(
                            stage.student_activities ??
                            stage.student_actions
                        )}
                    </div>
                </Col>
            </Row>

            {stage.assessment && (
                <div
                    style={{
                        marginTop: 12,
                    }}
                >
                    <Text strong>
                        Assessment
                    </Text>

                    <div style={{ marginTop: 4 }}>
                        {renderFlexibleValue(
                            stage.assessment
                        )}
                    </div>
                </div>
            )}
        </Card>
    );
};

const MisconceptionList = ({
    value,
}) => {
    const items = safeArray(value);

    if (!items.length) {
        return (
            <Text type="secondary">
                No common misconceptions were provided.
            </Text>
        );
    }

    return (
        <Space
            direction="vertical"
            size={10}
            style={{ width: "100%" }}
        >
            {items.map((item, index) => {
                if (
                    typeof item === "string"
                ) {
                    return (
                        <Card
                            size="small"
                            key={`misconception-${index}`}
                            style={{
                                borderColor:
                                    COLORS.border,
                            }}
                        >
                            <Text>
                                {item}
                            </Text>
                        </Card>
                    );
                }

                const misconception =
                    valueToText(
                        item?.misconception ??
                        item?.error ??
                        item?.common_error
                    );

                const correction =
                    valueToText(
                        item?.correction ??
                        item?.correct_understanding ??
                        item?.teacher_response ??
                        item?.solution
                    );

                return (
                    <Card
                        size="small"
                        key={`misconception-${index}`}
                        style={{
                            borderColor:
                                COLORS.border,
                        }}
                    >
                        {misconception && (
                            <div
                                style={{
                                    marginBottom:
                                        correction
                                            ? 8
                                            : 0,
                                }}
                            >
                                <Text strong>
                                    Misconception
                                </Text>

                                <div>
                                    <Text>
                                        {
                                            misconception
                                        }
                                    </Text>
                                </div>
                            </div>
                        )}

                        {correction && (
                            <div>
                                <Text strong>
                                    Correction
                                </Text>

                                <div>
                                    <Text>
                                        {
                                            correction
                                        }
                                    </Text>
                                </div>
                            </div>
                        )}

                        {!misconception &&
                            !correction && (
                                renderFlexibleValue(
                                    item
                                )
                            )}
                    </Card>
                );
            })}
        </Space>
    );
};

const TeacherSupportMaterials = ({
    value,
}) => {
    if (!isObject(value)) {
        return (
            <Text type="secondary">
                No teacher support materials were generated.
            </Text>
        );
    }

    const createdByAi =
        value.created_by_ai;

    const usageNote =
        normalizeText(
            value.usage_note
        );

    const readingPassages =
        safeArray(
            value.reading_passages
        );

    const examples =
        safeArray(value.examples);

    return (
        <Space
            direction="vertical"
            size={14}
            style={{ width: "100%" }}
        >
            <div>
                <Space wrap>
                    <Tag
                        color={
                            createdByAi
                                ? "blue"
                                : "default"
                        }
                    >
                        {createdByAi
                            ? "AI-created support material"
                            : "Teacher support material"}
                    </Tag>
                </Space>
            </div>

            {usageNote && (
                <Card
                    size="small"
                    style={{
                        background:
                            COLORS.light,
                        borderColor:
                            COLORS.border,
                    }}
                >
                    <Text strong>
                        Usage Note
                    </Text>

                    <Paragraph
                        style={{
                            marginBottom: 0,
                            marginTop: 4,
                        }}
                    >
                        {usageNote}
                    </Paragraph>
                </Card>
            )}

            <div>
                <Text strong>
                    Reading Passages
                </Text>

                <div style={{ marginTop: 6 }}>
                    {readingPassages.length
                        ? renderSimpleList(
                            readingPassages
                        )
                        : (
                            <Text type="secondary">
                                No reading passages provided.
                            </Text>
                        )}
                </div>
            </div>

            <div>
                <Text strong>
                    Examples
                </Text>

                <div style={{ marginTop: 6 }}>
                    {examples.length
                        ? renderSimpleList(
                            examples
                        )
                        : (
                            <Text type="secondary">
                                No examples provided.
                            </Text>
                        )}
                </div>
            </div>
        </Space>
    );
};

const CurriculumTeacherWorkspacePopup = ({
    open,
    onClose,
    topic,
    academicYear,
}) => {
    const topicId = useMemo(
        () => getTopicId(topic),
        [topic]
    );

    const [generatePlanLoading, setGeneratePlanLoading] =
        useState(false);

    const [generateNoteLoading, setGenerateNoteLoading] =
        useState(false);

    const [generateQuizLoading, setGenerateQuizLoading] =
        useState(false);

    const [lessonPlan, setLessonPlan] =
        useState(null);

    const [lessonNote, setLessonNote] =
        useState(null);

    const [quiz, setQuiz] =
        useState(null);

    const [aiError, setAiError] =
        useState("");

    useEffect(() => {
        if (!open) {
            return;
        }

        setLessonPlan(null);
        setLessonNote(null);
        setQuiz(null);
        setAiError("");
    }, [open, topicId]);

    const curriculumName =
        getCurriculumName(topic);

    const subjectName =
        getSubjectName(topic);

    const gradeName =
        getGradeName(topic);

    const semesterName =
        getSemesterName(topic);

    const periodName =
        getPeriodName(topic);

    const topicName =
        getTopicName(topic);

    const planContent =
        useMemo(
            () => getAiContent(lessonPlan),
            [lessonPlan]
        );

    const noteContent =
        useMemo(
            () => getAiContent(lessonNote),
            [lessonNote]
        );

    const officialObjectives =
        topic?.objectives ??
        topic?.learning_objectives ??
        topic?.learning_outcomes ??
        [];

    const officialContent =
        topic?.content ??
        topic?.topic_content ??
        topic?.curriculum_content ??
        "";

    const officialCompetencies =
        topic?.competencies ??
        topic?.competency ??
        [];

    const assessmentGuidance =
        topic?.assessment_guidance ??
        topic?.assessment ??
        "";

    const teachingResources =
        topic?.teaching_resources ??
        topic?.resources ??
        [];

    const references =
        topic?.references ??
        topic?.reference ??
        [];

    const generateLessonPlan = async () => {
        if (!topicId) {
            message.error(
                "No curriculum topic was selected."
            );
            return;
        }

        setGeneratePlanLoading(true);
        setAiError("");

        try {
            const payload = {
                topic_id: topicId,
            };

            const response = await api.post(
                "/ai-lesson-plans/generate/",
                payload
            );

            const data =
                response?.data?.data ??
                response?.data?.lesson_plan ??
                response?.data;

            if (!data || typeof data !== "object") {
                throw new Error(
                    "The server returned an invalid lesson plan response."
                );
            }

            setLessonPlan(data);
            setLessonNote(null);
            setQuiz(null);

            message.success(
                "Lesson plan generated successfully."
            );
        } catch (error) {
            const errorMessage =
                getApiErrorMessage(
                    error,
                    "Unable to generate the lesson plan."
                );

            setAiError(errorMessage);

            message.error(
                errorMessage
            );
        } finally {
            setGeneratePlanLoading(false);
        }
    };

    const generateTeacherNotes = async () => {
        if (!topicId) {
            message.error(
                "No curriculum topic was selected."
            );
            return;
        }

        setGenerateNoteLoading(true);
        setAiError("");

        try {
            const payload = {
                topic_id: topicId,
            };

            if (lessonPlan?.id) {
                payload.lesson_plan_id =
                    lessonPlan.id;
            }

            const response = await api.post(
                "/ai-teacher-notes/generate/",
                payload
            );

            const data =
                response?.data?.data ??
                response?.data?.teacher_notes ??
                response?.data;

            if (!data || typeof data !== "object") {
                throw new Error(
                    "The server returned an invalid teacher notes response."
                );
            }

            setLessonNote(data);

            message.success(
                "Teacher notes generated successfully."
            );
        } catch (error) {
            const errorMessage =
                getApiErrorMessage(
                    error,
                    "Unable to generate teacher notes."
                );

            setAiError(errorMessage);

            message.error(
                errorMessage
            );
        } finally {
            setGenerateNoteLoading(false);
        }
    };

    const generateQuiz = async () => {
        if (!topicId) {
            message.error(
                "No curriculum topic was selected."
            );
            return;
        }

        setGenerateQuizLoading(true);
        setAiError("");

        try {
            const payload = {
                topic_id: topicId,
            };

            if (lessonPlan?.id) {
                payload.lesson_plan_id =
                    lessonPlan.id;
            }

            if (lessonNote?.id) {
                payload.teacher_notes_id =
                    lessonNote.id;
            }

            const response = await api.post(
                "/ai-quizzes/generate/",
                payload
            );

            const data =
                response?.data?.data ??
                response?.data?.quiz ??
                response?.data;

            if (!data || typeof data !== "object") {
                throw new Error(
                    "The server returned an invalid quiz response."
                );
            }

            setQuiz(data);

            message.success(
                "Quiz generated successfully."
            );
        } catch (error) {
            const errorMessage =
                getApiErrorMessage(
                    error,
                    "Unable to generate quiz."
                );

            setAiError(errorMessage);

            message.error(
                errorMessage
            );
        } finally {
            setGenerateQuizLoading(false);
        }
    };

    const openPdf = (data) => {
        const pdfUrl = getPdfUrl(data);

        if (!pdfUrl) {
            message.info(
                "A PDF download is not available for this item yet."
            );
            return;
        }

        window.open(
            pdfUrl,
            "_blank",
            "noopener,noreferrer"
        );
    };

    const renderIntroduction = () => {
        const introduction =
            planContent?.introduction;

        if (!introduction) {
            return (
                <Text type="secondary">
                    No introduction was provided.
                </Text>
            );
        }

        if (typeof introduction === "string") {
            return (
                <Paragraph
                    style={{
                        marginBottom: 0,
                        whiteSpace:
                            "pre-wrap",
                    }}
                >
                    {introduction}
                </Paragraph>
            );
        }

        return (
            <Row gutter={[16, 12]}>
                {introduction.duration && (
                    <Col span={24}>
                        <Tag>
                            Duration:{" "}
                            {valueToText(
                                introduction.duration
                            )}
                        </Tag>
                    </Col>
                )}

                <Col
                    xs={24}
                    md={12}
                >
                    <Text strong>
                        Teacher Actions
                    </Text>

                    <div
                        style={{
                            marginTop: 5,
                        }}
                    >
                        {renderSimpleList(
                            introduction.teacher_actions ??
                            introduction.teacher_activities
                        )}
                    </div>
                </Col>

                <Col
                    xs={24}
                    md={12}
                >
                    <Text strong>
                        Student Actions
                    </Text>

                    <div
                        style={{
                            marginTop: 5,
                        }}
                    >
                        {renderSimpleList(
                            introduction.student_actions ??
                            introduction.student_activities
                        )}
                    </div>
                </Col>

                {introduction.content && (
                    <Col span={24}>
                        <Text strong>
                            Introduction
                        </Text>

                        <div
                            style={{
                                marginTop: 5,
                            }}
                        >
                            {renderFlexibleValue(
                                introduction.content
                            )}
                        </div>
                    </Col>
                )}
            </Row>
        );
    };

    const renderPlan = () => {
        if (!lessonPlan) {
            return (
                <Empty
                    description={
                        <>
                            <div>
                                No AI lesson plan has
                                been generated yet.
                            </div>

                            <Text
                                type="secondary"
                                style={{
                                    fontSize: 12,
                                }}
                            >
                                The AI uses the approved
                                curriculum topic as its
                                authoritative source.
                            </Text>
                        </>
                    }
                />
            );
        }

        const procedure =
            safeArray(
                planContent?.lesson_procedure
            );

        return (
            <>
                <Card
                    size="small"
                    style={{
                        marginBottom: 16,
                        borderColor:
                            COLORS.border,
                        background:
                            COLORS.light,
                    }}
                >
                    <Row gutter={[16, 12]}>
                        <Col
                            xs={24}
                            md={16}
                        >
                            <Text
                                type="secondary"
                                style={{
                                    fontSize: 12,
                                }}
                            >
                                GENERATED LESSON PLAN
                            </Text>

                            <Title
                                level={4}
                                style={{
                                    marginTop: 4,
                                    marginBottom: 6,
                                    color:
                                        COLORS.navy,
                                }}
                            >
                                {normalizeText(
                                    lessonPlan.title ??
                                    planContent.title
                                ) ||
                                    "Generated Lesson Plan"}
                            </Title>

                            <Space
                                wrap
                                size={[
                                    6,
                                    6,
                                ]}
                            >
                                {gradeName && (
                                    <Tag>
                                        {gradeName}
                                    </Tag>
                                )}

                                {subjectName && (
                                    <Tag>
                                        {subjectName}
                                    </Tag>
                                )}

                                {semesterName && (
                                    <Tag>
                                        {semesterName}
                                    </Tag>
                                )}

                                {periodName && (
                                    <Tag>
                                        {periodName}
                                    </Tag>
                                )}
                            </Space>
                        </Col>

                        <Col
                            xs={24}
                            md={8}
                            style={{
                                textAlign:
                                    "right",
                            }}
                        >
                            <Space
                                direction="vertical"
                                align="end"
                            >
                                {lessonPlan.duration && (
                                    <Tag color="blue">
                                        Duration:{" "}
                                        {
                                            lessonPlan.duration
                                        }
                                    </Tag>
                                )}

                                {lessonPlan.status && (
                                    <Tag
                                        color={
                                            lessonPlan.status ===
                                            "generated"
                                                ? "green"
                                                : "default"
                                        }
                                    >
                                        {
                                            lessonPlan.status
                                        }
                                    </Tag>
                                )}
                            </Space>
                        </Col>
                    </Row>

                    <Divider />

                    <Space
                        wrap
                        size={[
                            8,
                            8,
                        ]}
                    >
                        {lessonPlan.provider && (
                            <Tag>
                                Provider:{" "}
                                {
                                    lessonPlan.provider
                                }
                            </Tag>
                        )}

                        {lessonPlan.model_name && (
                            <Tag>
                                Model:{" "}
                                {
                                    lessonPlan.model_name
                                }
                            </Tag>
                        )}

                        {lessonPlan.version && (
                            <Tag>
                                Version:{" "}
                                {
                                    lessonPlan.version
                                }
                            </Tag>
                        )}

                        {lessonPlan.generation_id && (
                            <Tag>
                                Generation:{" "}
                                {
                                    lessonPlan.generation_id
                                }
                            </Tag>
                        )}
                    </Space>
                </Card>

                <Alert
                    type="info"
                    showIcon
                    icon={
                        <SafetyCertificateOutlined />
                    }
                    message="Curriculum integrity"
                    description="Official curriculum information remains authoritative. AI-generated content expands the approved topic into practical teaching activities and materials; it does not replace or alter the official curriculum data."
                    style={{
                        marginBottom: 16,
                    }}
                />

                <CurriculumSection
                    title="Lesson Overview"
                    icon={
                        <BookOutlined
                            style={{
                                color:
                                    COLORS.blue,
                            }}
                        />
                    }
                >
                    {renderFlexibleValue(
                        planContent.lesson_overview
                    )}
                </CurriculumSection>

                <Row gutter={[16, 0]}>
                    <Col
                        xs={24}
                        lg={12}
                    >
                        <CurriculumSection
                            title="Objectives"
                            icon={
                                <CheckCircleOutlined
                                    style={{
                                        color:
                                            COLORS.green,
                                    }}
                                />
                            }
                        >
                            {renderSimpleList(
                                planContent.objectives
                            )}
                        </CurriculumSection>
                    </Col>

                    <Col
                        xs={24}
                        lg={12}
                    >
                        <CurriculumSection
                            title="Previous Knowledge"
                            icon={
                                <SolutionOutlined
                                    style={{
                                        color:
                                            COLORS.orange,
                                    }}
                                />
                            }
                        >
                            {renderFlexibleValue(
                                planContent.previous_knowledge
                            )}
                        </CurriculumSection>
                    </Col>
                </Row>

                <Row gutter={[16, 0]}>
                    <Col
                        xs={24}
                        lg={12}
                    >
                        <CurriculumSection
                            title="Key Vocabulary"
                            icon={
                                <FileTextOutlined
                                    style={{
                                        color:
                                            COLORS.navy,
                                    }}
                                />
                            }
                        >
                            {renderSimpleList(
                                planContent.key_vocabulary
                            )}
                        </CurriculumSection>
                    </Col>

                    <Col
                        xs={24}
                        lg={12}
                    >
                        <CurriculumSection
                            title="Materials"
                            icon={
                                <BookOutlined
                                    style={{
                                        color:
                                            COLORS.khaki,
                                    }}
                                />
                            }
                        >
                            {renderSimpleList(
                                planContent.materials
                            )}
                        </CurriculumSection>
                    </Col>
                </Row>

                <CurriculumSection
                    title="Teacher Preparation"
                    icon={
                        <SolutionOutlined
                            style={{
                                color:
                                    COLORS.orange,
                            }}
                        />
                    }
                >
                    {renderFlexibleValue(
                        planContent.teacher_preparation
                    )}
                </CurriculumSection>

                <CurriculumSection
                    title="Introduction"
                    icon={
                        <BookOutlined
                            style={{
                                color:
                                    COLORS.blue,
                            }}
                        />
                    }
                >
                    {renderIntroduction()}
                </CurriculumSection>

                <CurriculumSection
                    title="Lesson Procedure"
                    icon={
                        <SolutionOutlined
                            style={{
                                color:
                                    COLORS.navy,
                            }}
                        />
                    }
                    extra={
                        procedure.length > 0 ? (
                            <Tag>
                                {procedure.length}{" "}
                                stage
                                {procedure.length ===
                                1
                                    ? ""
                                    : "s"}
                            </Tag>
                        ) : null
                    }
                >
                    {procedure.length ? (
                        procedure.map(
                            (stage, index) => (
                                <LessonProcedureStage
                                    key={`stage-${index}`}
                                    stage={stage}
                                    index={index}
                                />
                            )
                        )
                    ) : (
                        <Text type="secondary">
                            No lesson procedure stages were provided.
                        </Text>
                    )}
                </CurriculumSection>

                <Row gutter={[16, 0]}>
                    <Col
                        xs={24}
                        lg={12}
                    >
                        <CurriculumSection
                            title="Teacher Activities"
                            icon={
                                <SolutionOutlined
                                    style={{
                                        color:
                                            COLORS.orange,
                                    }}
                                />
                            }
                        >
                            {renderSimpleList(
                                planContent.teacher_activities
                            )}
                        </CurriculumSection>
                    </Col>

                    <Col
                        xs={24}
                        lg={12}
                    >
                        <CurriculumSection
                            title="Student Activities"
                            icon={
                                <BookOutlined
                                    style={{
                                        color:
                                            COLORS.green,
                                    }}
                                />
                            }
                        >
                            {renderSimpleList(
                                planContent.student_activities
                            )}
                        </CurriculumSection>
                    </Col>
                </Row>

                <Row gutter={[16, 0]}>
                    <Col
                        xs={24}
                        lg={12}
                    >
                        <CurriculumSection
                            title="Guided Practice"
                            icon={
                                <SolutionOutlined
                                    style={{
                                        color:
                                            COLORS.blue,
                                    }}
                                />
                            }
                        >
                            {renderFlexibleValue(
                                planContent.guided_practice
                            )}
                        </CurriculumSection>
                    </Col>

                    <Col
                        xs={24}
                        lg={12}
                    >
                        <CurriculumSection
                            title="Independent Practice"
                            icon={
                                <CheckCircleOutlined
                                    style={{
                                        color:
                                            COLORS.green,
                                    }}
                                />
                            }
                        >
                            {renderFlexibleValue(
                                planContent.independent_practice
                            )}
                        </CurriculumSection>
                    </Col>
                </Row>

                <CurriculumSection
                    title="Assessment"
                    icon={
                        <CheckCircleOutlined
                            style={{
                                color:
                                    COLORS.green,
                            }}
                        />
                    }
                >
                    {renderFlexibleValue(
                        planContent.assessment
                    )}
                </CurriculumSection>

                <CurriculumSection
                    title="Differentiation"
                    icon={
                        <SolutionOutlined
                            style={{
                                color:
                                    COLORS.orange,
                            }}
                        />
                    }
                >
                    {renderFlexibleValue(
                        planContent.differentiation
                    )}
                </CurriculumSection>

                <CurriculumSection
                    title="Common Misconceptions"
                    icon={
                        <ExperimentOutlined
                            style={{
                                color:
                                    COLORS.red,
                            }}
                        />
                    }
                >
                    <MisconceptionList
                        value={
                            planContent.common_misconceptions
                        }
                    />
                </CurriculumSection>

                <Row gutter={[16, 0]}>
                    <Col
                        xs={24}
                        lg={12}
                    >
                        <CurriculumSection
                            title="Remediation"
                            icon={
                                <ReloadOutlined
                                    style={{
                                        color:
                                            COLORS.orange,
                                    }}
                                />
                            }
                        >
                            {renderFlexibleValue(
                                planContent.remediation
                            )}
                        </CurriculumSection>
                    </Col>

                    <Col
                        xs={24}
                        lg={12}
                    >
                        <CurriculumSection
                            title="Extension"
                            icon={
                                <ExperimentOutlined
                                    style={{
                                        color:
                                            COLORS.blue,
                                    }}
                                />
                            }
                        >
                            {renderFlexibleValue(
                                planContent.extension
                            )}
                        </CurriculumSection>
                    </Col>
                </Row>

                <CurriculumSection
                    title="Homework"
                    icon={
                        <FileTextOutlined
                            style={{
                                color:
                                    COLORS.navy,
                            }}
                        />
                    }
                >
                    {renderFlexibleValue(
                        planContent.homework
                    )}
                </CurriculumSection>

                <CurriculumSection
                    title="Conclusion"
                    icon={
                        <CheckCircleOutlined
                            style={{
                                color:
                                    COLORS.green,
                            }}
                        />
                    }
                >
                    {renderFlexibleValue(
                        planContent.conclusion
                    )}
                </CurriculumSection>

                <CurriculumSection
                    title="Teacher Reflection"
                    icon={
                        <SolutionOutlined
                            style={{
                                color:
                                    COLORS.gray,
                            }}
                        />
                    }
                >
                    {renderFlexibleValue(
                        planContent.teacher_reflection
                    )}
                </CurriculumSection>

                <CurriculumSection
                    title="Resources"
                    icon={
                        <BookOutlined
                            style={{
                                color:
                                    COLORS.khaki,
                            }}
                        />
                    }
                >
                    {renderSimpleList(
                        planContent.resources
                    )}
                </CurriculumSection>

                <CurriculumSection
                    title="Curriculum Alignment"
                    icon={
                        <SafetyCertificateOutlined
                            style={{
                                color:
                                    COLORS.navy,
                            }}
                        />
                    }
                >
                    {renderFlexibleValue(
                        planContent.curriculum_alignment
                    )}
                </CurriculumSection>

                <CurriculumSection
                    title="Teacher Support Materials"
                    icon={
                        <FileTextOutlined
                            style={{
                                color:
                                    COLORS.orange,
                            }}
                        />
                    }
                >
                    <TeacherSupportMaterials
                        value={
                            planContent.teacher_support_materials
                        }
                    />
                </CurriculumSection>
            </>
        );
    };

    const renderTeacherNotes = () => {
        if (!lessonNote) {
            return (
                <Empty
                    description="No AI teacher notes have been generated yet."
                />
            );
        }

        return (
            <>
                <Card
                    size="small"
                    style={{
                        marginBottom: 16,
                        borderColor:
                            COLORS.border,
                        background:
                            COLORS.light,
                    }}
                >
                    <Title
                        level={4}
                        style={{
                            marginBottom: 6,
                            color:
                                COLORS.navy,
                        }}
                    >
                        {normalizeText(
                            lessonNote.title ??
                            noteContent.title
                        ) ||
                            "Teacher Notes"}
                    </Title>

                    <Space wrap>
                        {lessonNote.provider && (
                            <Tag>
                                Provider:{" "}
                                {
                                    lessonNote.provider
                                }
                            </Tag>
                        )}

                        {lessonNote.model_name && (
                            <Tag>
                                Model:{" "}
                                {
                                    lessonNote.model_name
                                }
                            </Tag>
                        )}

                        {lessonNote.status && (
                            <Tag>
                                {
                                    lessonNote.status
                                }
                            </Tag>
                        )}
                    </Space>
                </Card>

                <Row gutter={[16, 0]}>
                    <Col
                        xs={24}
                        lg={12}
                    >
                        <CurriculumSection
                            title="Overview"
                            icon={
                                <BookOutlined
                                    style={{
                                        color:
                                            COLORS.blue,
                                    }}
                                />
                            }
                        >
                            {renderFlexibleValue(
                                noteContent.overview ??
                                lessonNote.overview
                            )}
                        </CurriculumSection>
                    </Col>

                    <Col
                        xs={24}
                        lg={12}
                    >
                        <CurriculumSection
                            title="Key Concepts"
                            icon={
                                <SolutionOutlined
                                    style={{
                                        color:
                                            COLORS.navy,
                                    }}
                                />
                            }
                        >
                            {renderSimpleList(
                                noteContent.key_concepts ??
                                lessonNote.key_concepts
                            )}
                        </CurriculumSection>
                    </Col>
                </Row>

                <CurriculumSection
                    title="Definitions"
                    icon={
                        <FileTextOutlined
                            style={{
                                color:
                                    COLORS.orange,
                            }}
                        />
                    }
                >
                    {renderFlexibleValue(
                        noteContent.definitions ??
                        lessonNote.definitions
                    )}
                </CurriculumSection>

                <CurriculumSection
                    title="Detailed Explanation"
                    icon={
                        <BookOutlined
                            style={{
                                color:
                                    COLORS.blue,
                            }}
                        />
                    }
                >
                    {renderFlexibleValue(
                        noteContent.detailed_explanation ??
                        lessonNote.detailed_explanation
                    )}
                </CurriculumSection>

                <Row gutter={[16, 0]}>
                    <Col
                        xs={24}
                        lg={12}
                    >
                        <CurriculumSection
                            title="Examples"
                            icon={
                                <ExperimentOutlined
                                    style={{
                                        color:
                                            COLORS.green,
                                    }}
                                />
                            }
                        >
                            {renderSimpleList(
                                noteContent.examples ??
                                lessonNote.examples
                            )}
                        </CurriculumSection>
                    </Col>

                    <Col
                        xs={24}
                        lg={12}
                    >
                        <CurriculumSection
                            title="Teaching Points"
                            icon={
                                <SolutionOutlined
                                    style={{
                                        color:
                                            COLORS.orange,
                                    }}
                                />
                            }
                        >
                            {renderSimpleList(
                                noteContent.teaching_points ??
                                lessonNote.teaching_points
                            )}
                        </CurriculumSection>
                    </Col>
                </Row>

                <CurriculumSection
                    title="Misconceptions"
                    icon={
                        <ExperimentOutlined
                            style={{
                                color:
                                    COLORS.red,
                            }}
                        />
                    }
                >
                    <MisconceptionList
                        value={
                            noteContent.misconceptions ??
                            lessonNote.misconceptions
                        }
                    />
                </CurriculumSection>

                <Row gutter={[16, 0]}>
                    <Col
                        xs={24}
                        lg={12}
                    >
                        <CurriculumSection
                            title="Remediation"
                            icon={
                                <ReloadOutlined
                                    style={{
                                        color:
                                            COLORS.orange,
                                    }}
                                />
                            }
                        >
                            {renderFlexibleValue(
                                noteContent.remediation ??
                                lessonNote.remediation
                            )}
                        </CurriculumSection>
                    </Col>

                    <Col
                        xs={24}
                        lg={12}
                    >
                        <CurriculumSection
                            title="Discussion Questions"
                            icon={
                                <BookOutlined
                                    style={{
                                        color:
                                            COLORS.blue,
                                    }}
                                />
                            }
                        >
                            {renderSimpleList(
                                noteContent.discussion_questions ??
                                lessonNote.discussion_questions
                            )}
                        </CurriculumSection>
                    </Col>
                </Row>

                <CurriculumSection
                    title="Practical Activities"
                    icon={
                        <ExperimentOutlined
                            style={{
                                color:
                                    COLORS.green,
                            }}
                        />
                    }
                >
                    {renderFlexibleValue(
                        noteContent.practical_activities ??
                        lessonNote.practical_activities
                    )}
                </CurriculumSection>

                <CurriculumSection
                    title="Summary"
                    icon={
                        <CheckCircleOutlined
                            style={{
                                color:
                                    COLORS.green,
                            }}
                        />
                    }
                >
                    {renderFlexibleValue(
                        noteContent.summary ??
                        lessonNote.summary
                    )}
                </CurriculumSection>

                <CurriculumSection
                    title="Curriculum Alignment"
                    icon={
                        <SafetyCertificateOutlined
                            style={{
                                color:
                                    COLORS.navy,
                            }}
                        />
                    }
                >
                    {renderFlexibleValue(
                        noteContent.curriculum_alignment ??
                        lessonNote.curriculum_alignment
                    )}
                </CurriculumSection>
            </>
        );
    };

    const renderQuiz = () => {
        if (!quiz) {
            return (
                <Empty
                    description="No AI quiz has been generated yet."
                />
            );
        }

        const questions =
            Array.isArray(
                quiz.questions
            )
                ? quiz.questions
                : [];

        return (
            <>
                <Card
                    size="small"
                    style={{
                        marginBottom: 16,
                        borderColor:
                            COLORS.border,
                        background:
                            COLORS.light,
                    }}
                >
                    <Title
                        level={4}
                        style={{
                            marginBottom: 6,
                            color:
                                COLORS.navy,
                        }}
                    >
                        {normalizeText(
                            quiz.title
                        ) ||
                            "Generated Quiz"}
                    </Title>

                    {quiz.instructions && (
                        <Paragraph
                            style={{
                                marginBottom: 8,
                            }}
                        >
                            {
                                quiz.instructions
                            }
                        </Paragraph>
                    )}

                    <Tag color="blue">
                        {questions.length}{" "}
                        question
                        {questions.length ===
                        1
                            ? ""
                            : "s"}
                    </Tag>
                </Card>

                <Space
                    direction="vertical"
                    size={12}
                    style={{
                        width: "100%",
                    }}
                >
                    {questions.map(
                        (
                            question,
                            index
                        ) => {
                            const options =
                                safeArray(
                                    question.options
                                );

                            return (
                                <Card
                                    key={
                                        question.id ??
                                        `quiz-question-${index}`
                                    }
                                    size="small"
                                    title={
                                        <Space>
                                            <Badge
                                                count={
                                                    question.order ??
                                                    index +
                                                        1
                                                }
                                                style={{
                                                    backgroundColor:
                                                        COLORS.navy,
                                                }}
                                            />

                                            <Text strong>
                                                {normalizeText(
                                                    question.question
                                                )}
                                            </Text>
                                        </Space>
                                    }
                                    style={{
                                        borderColor:
                                            COLORS.border,
                                    }}
                                >
                                    {options.length >
                                        0 && (
                                        <div
                                            style={{
                                                marginBottom:
                                                    12,
                                            }}
                                        >
                                            <Text strong>
                                                Options
                                            </Text>

                                            <List
                                                size="small"
                                                dataSource={
                                                    options
                                                }
                                                renderItem={(
                                                    option,
                                                    optionIndex
                                                ) => (
                                                    <List.Item
                                                        key={`option-${optionIndex}`}
                                                    >
                                                        <Text>
                                                            {String.fromCharCode(
                                                                65 +
                                                                    optionIndex
                                                            )}
                                                            .{" "}
                                                            {valueToText(
                                                                option
                                                            )}
                                                        </Text>
                                                    </List.Item>
                                                )}
                                            />
                                        </div>
                                    )}

                                    {question.correct_answer !==
                                        undefined &&
                                        question.correct_answer !==
                                            null && (
                                            <Alert
                                                type="success"
                                                showIcon
                                                message="Correct Answer"
                                                description={valueToText(
                                                    question.correct_answer
                                                )}
                                                style={{
                                                    marginBottom:
                                                        10,
                                                }}
                                            />
                                        )}

                                    {question.explanation && (
                                        <div>
                                            <Text strong>
                                                Explanation
                                            </Text>

                                            <Paragraph
                                                style={{
                                                    marginBottom: 0,
                                                    marginTop: 4,
                                                }}
                                            >
                                                {valueToText(
                                                    question.explanation
                                                )}
                                            </Paragraph>
                                        </div>
                                    )}

                                    {question.points !==
                                        undefined &&
                                        question.points !==
                                            null && (
                                            <Tag
                                                style={{
                                                    marginTop:
                                                        10,
                                                }}
                                            >
                                                Points:{" "}
                                                {
                                                    question.points
                                                }
                                            </Tag>
                                        )}
                                </Card>
                            );
                        }
                    )}
                </Space>
            </>
        );
    };

    const footer = (
        <div
            style={{
                display: "flex",
                justifyContent:
                    "space-between",
                gap: 8,
                flexWrap: "wrap",
                width: "100%",
            }}
        >
            <Space wrap>
                <Button
                    onClick={onClose}
                >
                    Close
                </Button>
            </Space>

            <Space wrap>
                {lessonPlan && (
                    <Button
                        icon={
                            <FilePdfOutlined />
                        }
                        onClick={() =>
                            openPdf(
                                lessonPlan
                            )
                        }
                    >
                        Lesson Plan PDF
                    </Button>
                )}

                {lessonNote && (
                    <Button
                        icon={
                            <FilePdfOutlined />
                        }
                        onClick={() =>
                            openPdf(
                                lessonNote
                            )
                        }
                    >
                        Teacher Notes PDF
                    </Button>
                )}
            </Space>
        </div>
    );

    return (
        <>
            <Modal
                open={open}
                onCancel={onClose}
                footer={footer}
                width="min(1200px, 96vw)"
                title={
                    <Space
                        align="center"
                        size={10}
                    >
                        <RobotOutlined
                            style={{
                                color:
                                    COLORS.orange,
                            }}
                        />

                        <div>
                            <div
                                style={{
                                    fontWeight: 700,
                                    color:
                                        COLORS.navy,
                                }}
                            >
                                Curriculum Teacher Workspace
                            </div>

                            <Text
                                type="secondary"
                                style={{
                                    fontSize: 12,
                                }}
                            >
                                AI-assisted lesson preparation
                            </Text>
                        </div>
                    </Space>
                }
                styles={{
                    body: {
                        padding: 0,
                    },
                }}
            >
                <div
                    style={{
                        maxHeight:
                            "calc(100vh - 190px)",
                        overflowY:
                            "auto",
                        padding:
                            "16px 18px 24px",
                        background:
                            COLORS.white,
                    }}
                >
                    {!topicId ? (
                        <Empty
                            description="No curriculum topic selected."
                        />
                    ) : (
                        <>
                            <Card
                                size="small"
                                style={{
                                    marginBottom: 16,
                                    borderColor:
                                        COLORS.border,
                                    borderRadius: 10,
                                }}
                            >
                                <Row
                                    gutter={[
                                        16,
                                        12,
                                    ]}
                                    align="middle"
                                >
                                    <Col
                                        xs={24}
                                        lg={16}
                                    >
                                        <Text
                                            type="secondary"
                                            style={{
                                                fontSize: 11,
                                                textTransform:
                                                    "uppercase",
                                                letterSpacing:
                                                    0.5,
                                            }}
                                        >
                                            Selected Curriculum Topic
                                        </Text>

                                        <Title
                                            level={4}
                                            style={{
                                                marginTop: 3,
                                                marginBottom: 8,
                                                color:
                                                    COLORS.navy,
                                            }}
                                        >
                                            {topicName ||
                                                "Curriculum Topic"}
                                        </Title>

                                        <Space
                                            wrap
                                            size={[
                                                6,
                                                6,
                                            ]}
                                        >
                                            {curriculumName && (
                                                <Tag>
                                                    {
                                                        curriculumName
                                                    }
                                                </Tag>
                                            )}

                                            {subjectName && (
                                                <Tag>
                                                    {
                                                        subjectName
                                                    }
                                                </Tag>
                                            )}

                                            {gradeName && (
                                                <Tag>
                                                    {
                                                        gradeName
                                                    }
                                                </Tag>
                                            )}

                                            {semesterName && (
                                                <Tag>
                                                    {
                                                        semesterName
                                                    }
                                                </Tag>
                                            )}

                                            {periodName && (
                                                <Tag>
                                                    {
                                                        periodName
                                                    }
                                                </Tag>
                                            )}
                                        </Space>
                                    </Col>

                                    <Col
                                        xs={24}
                                        lg={8}
                                        style={{
                                            textAlign:
                                                "right",
                                        }}
                                    >
                                        <Space
                                            wrap
                                        >
                                            <Button
                                                type="primary"
                                                icon={
                                                    <RobotOutlined />
                                                }
                                                loading={
                                                    generatePlanLoading
                                                }
                                                disabled={
                                                    generatePlanLoading ||
                                                    !topicId
                                                }
                                                onClick={
                                                    generateLessonPlan
                                                }
                                                style={{
                                                    background:
                                                        COLORS.orange,
                                                    borderColor:
                                                        COLORS.orange,
                                                }}
                                            >
                                                Generate Lesson Plan
                                            </Button>

                                            <Button
                                                icon={
                                                    <SolutionOutlined />
                                                }
                                                loading={
                                                    generateNoteLoading
                                                }
                                                disabled={
                                                    generateNoteLoading ||
                                                    !topicId
                                                }
                                                onClick={
                                                    generateTeacherNotes
                                                }
                                            >
                                                Teacher Notes
                                            </Button>

                                            <Button
                                                icon={
                                                    <ExperimentOutlined />
                                                }
                                                loading={
                                                    generateQuizLoading
                                                }
                                                disabled={
                                                    generateQuizLoading ||
                                                    !topicId
                                                }
                                                onClick={
                                                    generateQuiz
                                                }
                                            >
                                                Generate Quiz
                                            </Button>
                                        </Space>
                                    </Col>
                                </Row>
                            </Card>

                            {aiError && (
                                <Alert
                                    type="error"
                                    showIcon
                                    closable
                                    onClose={() =>
                                        setAiError(
                                            ""
                                        )
                                    }
                                    message="AI generation error"
                                    description={
                                        aiError
                                    }
                                    style={{
                                        marginBottom:
                                            16,
                                    }}
                                />
                            )}

                            <CurriculumSection
                                title="Official Curriculum Information"
                                icon={
                                    <SafetyCertificateOutlined
                                        style={{
                                            color:
                                                COLORS.navy,
                                        }}
                                    />
                                }
                            >
                                <Alert
                                    type="info"
                                    showIcon
                                    message="Official curriculum source"
                                    description="The information below comes from the selected curriculum topic. AI generation must preserve these official curriculum facts."
                                    style={{
                                        marginBottom:
                                            14,
                                    }}
                                />

                                <Row
                                    gutter={[
                                        16,
                                        16,
                                    ]}
                                >
                                    <Col
                                        xs={24}
                                        lg={12}
                                    >
                                        <Text strong>
                                            Topic
                                        </Text>

                                        <Paragraph
                                            style={{
                                                marginBottom: 0,
                                                marginTop: 4,
                                            }}
                                        >
                                            {topicName ||
                                                "Not provided"}
                                        </Paragraph>
                                    </Col>

                                    <Col
                                        xs={24}
                                        lg={12}
                                    >
                                        <Text strong>
                                            Assessment Guidance
                                        </Text>

                                        <div
                                            style={{
                                                marginTop: 4,
                                            }}
                                        >
                                            {renderFlexibleValue(
                                                assessmentGuidance
                                            )}
                                        </div>
                                    </Col>

                                    <Col
                                        xs={24}
                                        lg={12}
                                    >
                                        <Text strong>
                                            Curriculum Content
                                        </Text>

                                        <div
                                            style={{
                                                marginTop: 4,
                                            }}
                                        >
                                            {renderFlexibleValue(
                                                officialContent
                                            )}
                                        </div>
                                    </Col>

                                    <Col
                                        xs={24}
                                        lg={12}
                                    >
                                        <Text strong>
                                            Official Objectives
                                        </Text>

                                        <div
                                            style={{
                                                marginTop: 4,
                                            }}
                                        >
                                            {renderSimpleList(
                                                officialObjectives
                                            )}
                                        </div>
                                    </Col>

                                    <Col
                                        xs={24}
                                        lg={12}
                                    >
                                        <Text strong>
                                            Competencies
                                        </Text>

                                        <div
                                            style={{
                                                marginTop: 4,
                                            }}
                                        >
                                            {renderSimpleList(
                                                officialCompetencies
                                            )}
                                        </div>
                                    </Col>

                                    <Col
                                        xs={24}
                                        lg={12}
                                    >
                                        <Text strong>
                                            Teaching Resources
                                        </Text>

                                        <div
                                            style={{
                                                marginTop: 4,
                                            }}
                                        >
                                            {renderSimpleList(
                                                teachingResources
                                            )}
                                        </div>
                                    </Col>

                                    <Col
                                        xs={24}
                                    >
                                        <Text strong>
                                            References
                                        </Text>

                                        <div
                                            style={{
                                                marginTop: 4,
                                            }}
                                        >
                                            {renderSimpleList(
                                                references
                                            )}
                                        </div>
                                    </Col>
                                </Row>
                            </CurriculumSection>

                            <Divider />

                            <div
                                style={{
                                    marginBottom:
                                        12,
                                }}
                            >
                                <Title
                                    level={4}
                                    style={{
                                        marginBottom:
                                            4,
                                        color:
                                            COLORS.navy,
                                    }}
                                >
                                    AI Lesson Plan
                                </Title>

                                <Text type="secondary">
                                    The generated plan is
                                    expanded from the
                                    approved curriculum
                                    topic and keeps
                                    curriculum facts
                                    separate from AI-created
                                    instructional material.
                                </Text>
                            </div>

                            {generatePlanLoading ? (
                                <Card
                                    style={{
                                        marginBottom:
                                            16,
                                        textAlign:
                                            "center",
                                        borderColor:
                                            COLORS.border,
                                    }}
                                >
                                    <Spin size="large" />

                                    <div
                                        style={{
                                            marginTop:
                                                12,
                                        }}
                                    >
                                        <Text>
                                            Generating the
                                            lesson plan from
                                            the approved
                                            curriculum topic...
                                        </Text>
                                    </div>
                                </Card>
                            ) : (
                                renderPlan()
                            )}

                            {lessonPlan && (
                                <>
                                    <Divider />

                                    <div
                                        style={{
                                            display:
                                                "flex",
                                            justifyContent:
                                                "space-between",
                                            alignItems:
                                                "center",
                                            flexWrap:
                                                "wrap",
                                            gap: 8,
                                            marginBottom:
                                                12,
                                        }}
                                    >
                                        <div>
                                            <Title
                                                level={4}
                                                style={{
                                                    marginBottom:
                                                        3,
                                                    color:
                                                        COLORS.navy,
                                                }}
                                            >
                                                Teacher Notes
                                            </Title>

                                            <Text type="secondary">
                                                Detailed teacher
                                                preparation based
                                                on the generated
                                                lesson plan.
                                            </Text>
                                        </div>

                                        <Button
                                            icon={
                                                <SolutionOutlined />
                                            }
                                            loading={
                                                generateNoteLoading
                                            }
                                            onClick={
                                                generateTeacherNotes
                                            }
                                        >
                                            {lessonNote
                                                ? "Regenerate Teacher Notes"
                                                : "Generate Teacher Notes"}
                                        </Button>
                                    </div>

                                    {generateNoteLoading ? (
                                        <Card
                                            style={{
                                                marginBottom:
                                                    16,
                                                textAlign:
                                                    "center",
                                            }}
                                        >
                                            <Spin />

                                            <div
                                                style={{
                                                    marginTop:
                                                        10,
                                                }}
                                            >
                                                <Text>
                                                    Generating teacher
                                                    notes...
                                                </Text>
                                            </div>
                                        </Card>
                                    ) : (
                                        renderTeacherNotes()
                                    )}
                                </>
                            )}

                            <Divider />

                            <div
                                style={{
                                    display:
                                        "flex",
                                    justifyContent:
                                        "space-between",
                                    alignItems:
                                        "center",
                                    flexWrap:
                                        "wrap",
                                    gap: 8,
                                    marginBottom:
                                        12,
                                }}
                            >
                                <div>
                                    <Title
                                        level={4}
                                        style={{
                                            marginBottom:
                                                3,
                                            color:
                                                COLORS.navy,
                                        }}
                                    >
                                        AI Assessment Quiz
                                    </Title>

                                    <Text type="secondary">
                                        Generate assessment
                                        questions from the
                                        selected curriculum
                                        topic and available
                                        teaching materials.
                                    </Text>
                                </div>

                                <Button
                                    icon={
                                        <ExperimentOutlined />
                                    }
                                    loading={
                                        generateQuizLoading
                                    }
                                    onClick={
                                        generateQuiz
                                    }
                                >
                                    {quiz
                                        ? "Regenerate Quiz"
                                        : "Generate Quiz"}
                                </Button>
                            </div>

                            {generateQuizLoading ? (
                                <Card
                                    style={{
                                        textAlign:
                                            "center",
                                        marginBottom:
                                            16,
                                    }}
                                >
                                    <Spin />

                                    <div
                                        style={{
                                            marginTop:
                                                10,
                                        }}
                                    >
                                        <Text>
                                            Generating quiz...
                                        </Text>
                                    </div>
                                </Card>
                            ) : (
                                renderQuiz()
                            )}

                            {lessonPlan && (
                                <div
                                    style={{
                                        position:
                                            "absolute",
                                        width: 1,
                                        height: 1,
                                        overflow:
                                            "hidden",
                                        pointerEvents:
                                            "none",
                                        opacity: 0,
                                    }}
                                    aria-hidden="true"
                                >
                                    <AiLessonPlanPDF
                                        lessonPlan={
                                            lessonPlan 
                                        }
                                        topic={
                                            topic
                                        }
                                        academicYear={
                                            academicYear
                                        }
                                    />
                                </div>
                            )}

                            {lessonNote && (
                                <div
                                    style={{
                                        position:
                                            "absolute",
                                        width: 1,
                                        height: 1,
                                        overflow:
                                            "hidden",
                                        pointerEvents:
                                            "none",
                                        opacity: 0,
                                    }}
                                    aria-hidden="true"
                                >
                                    <AiTeacherNotesPDF
                                        teacherNotes={
                                            lessonNote
                                        }
                                        topic={
                                            topic
                                        }
                                        academicYear={
                                            academicYear
                                        }
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>
            </Modal>
        </>
    );
};

export default CurriculumTeacherWorkspacePopup;

